/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { UserRoleRepresentation } from '@veeam-vspc/models/web-controllers';
import { action, makeObservable } from 'mobx';

import { BaseCoreModule } from '../base-core-module';
import {
    mainFileTransportStrategy,
    mainRestServerTransportStrategy,
    mainStreamUploadFileRestTransportStrategy,
    mainStreamUploadFileTransportStrategy,
    mainWebServerTransportStrategy,
} from '../../strategies';
import { AppStates, SharedStrategies } from '../../enums';
import { NotificationDefaultTitles, NotificationDialogsTextKeys } from '../../services/notification';
import { AppStorage } from '../../helpers/storages';
import { HttpRequestService, TransportStrategies } from '../../core';

import type { AppDataStore } from '../../stores/app-data';
import type { PreparedPortalFilters } from '../../addons/portal-filters-addon';
import type { BaseLang, RequestErrorResponse } from '../../interfaces';
import type { MainCoreModuleConfig } from './interfaces';

export class MainCoreModule extends BaseCoreModule<RequestErrorResponse | Error> {

    constructor(pluginPortalCoreConfig: MainCoreModuleConfig) {
        const {
            transportConfig,
            fileTransportConfig,
            ...restConfig
        } = pluginPortalCoreConfig;
        const httpRequestService = process.env.NODE_ENV === 'test'
            ? (pluginPortalCoreConfig as any).fakeHttpRequestService // For JEST test set fake service
            : new HttpRequestService();

        super({
            ...restConfig,
            httpRequestService,
            transportConfig: {
                ...transportConfig,
                strategies: {
                    [TransportStrategies.RestServer]: mainRestServerTransportStrategy,
                    [TransportStrategies.WebServer]: mainWebServerTransportStrategy,
                },
                isErrorResponseData: resp => (resp as RequestErrorResponse).errors?.length > 0,
            },
            fileTransportConfig: {
                ...fileTransportConfig,
                strategies: {
                    [TransportStrategies.WebServer]: mainWebServerTransportStrategy,
                    [TransportStrategies.WebServerDownload]: mainFileTransportStrategy,
                    [TransportStrategies.StreamFileUpload]: mainStreamUploadFileTransportStrategy,
                    [TransportStrategies.StreamFileUploadRest]: mainStreamUploadFileRestTransportStrategy,
                },
            },
        });

        makeObservable(this);

        this.strategy = SharedStrategies.Main;
        this.transportServiceConfig.commands.getPortalFilters = () => this.getPortalFiltersCommand() as any;
        this.fileTransportServiceConfig.commands.getPortalFilters = () => this.getPortalFiltersCommand() as any;

        this.createServices();
    }

    init(): Promise<void> {
        return (
            this.customCheckAuthorization
                ? this.customCheckAuthorization(this.suppliers, this.getServices())
                : this.tokenManagerService.checkAuthorization()
        )
            .then(() => this.loadGlobalData(false))
            .catch(() => this.loadGlobalData(true))
            .then(() => {
                const newAppState = this.appDataStore.logged && this.appDataStore.portalUser
                    ? AppStates.LoggedIn
                    : AppStates.Unauthorized;

                this.setAppState(newAppState);

                if (newAppState === AppStates.LoggedIn) {
                    this.tokenManagerService.startTimer(this.appDataStore.sessionExpirationTime);
                    this.addons.portalPreferences?.startPreferencesUpdater();
                    this.appHealth.resetHp();
                }
            });
    }

    getPortalFiltersCommand(): Promise<PreparedPortalFilters | null> {
        const { portalUser } = this.appDataStore;
        const { portalFilters } = this.addons;
        const isAdmin = portalUser ? portalUser.isPortalOrSiteAdmin() : false;

        return Promise.resolve(portalFilters ? portalFilters.getFilters() : null)
            .then((filters) => {
                if (!isAdmin && filters?.siteFilter) {
                    // delete a siteFilter parameter if we aren't an admin
                    delete filters.siteFilter;
                }

                return filters;
            });
    }

    @action.bound
    protected loadGlobalData(noAuth: boolean): Promise<void> {
        return this.loadLoginData(noAuth)
            .then((appDataStore) => {
                this.setAppDataStore(appDataStore);

                if (!this.configuratorService.isUserRoleAllowed(appDataStore.portalUser?.userRole ?? UserRoleRepresentation.Unknown)) {
                    window.location.assign(this.getLoginPath());
                    return Promise.reject(); // Only one case with reject
                }

                return this.loadUserApp(this.appDataStore);
            })
            .then(() => this.loadAddonsData(noAuth))
            .then(() => {
                if (this.returnUrlManagerService && this.appDataStore.portalUser) {
                    return this.returnUrlManagerService.handleReturnUrls(this.currentLang as unknown as Record<string, string>);
                }
            })
            .then(() => {
                if (this.appEventHandlers.onBeforeLoadingEnd) {
                    return this.appEventHandlers.onBeforeLoadingEnd(this.getCoreModuleContext());
                }
            })
            .catch((err) => {
                err && console.error(err);
                // Can get here from only one place when user NOT allowed
                !noAuth && this.resetAuthData();
                this.setAppState(AppStates.Unauthorized);
            });
    }

    protected loadUserApp(appDataStore: AppDataStore) {
        this.notificationService.setTheme(appDataStore.theme);

        return Promise.all([
            this.langService.getLang(appDataStore.locale),
            this.configuratorService.getUserRoutes(appDataStore.portalUser?.userRole ?? UserRoleRepresentation.Unknown),
        ])
            .then(([lang, userRoutes]) => {
                this.setCurrentLang(lang as unknown as BaseLang);
                this.setUserRoutes(userRoutes);

                this.notificationService.setTexts({
                    [NotificationDialogsTextKeys.Ok]: lang.OK,
                    [NotificationDialogsTextKeys.Yes]: lang.YES,
                    [NotificationDialogsTextKeys.No]: lang.NO,
                    [NotificationDialogsTextKeys.Cancel]: lang.CANCEL,
                    [NotificationDialogsTextKeys.Close]: lang.CLOSE,
                    [NotificationDefaultTitles.Error]: lang.ERROR,
                    [NotificationDefaultTitles.Information]: lang.INFORMATION,
                    [NotificationDefaultTitles.Success]: lang.SUCCESS,
                    [NotificationDefaultTitles.Warning]: lang.WARNING,
                });

                if (appDataStore.portalUser) {
                    const { companyPrefix, productPrefix } = this.prefixes;

                    this.setAppStorage(new AppStorage(companyPrefix, productPrefix, appDataStore.portalUser.instanceUid));
                }
            });
    }

}
